import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../css/components/ContactUs.css";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as PhoneIcon } from "../../assets/icons/Contact.svg";
import { ReactComponent as EmailIcon } from "../../assets/icons/email1.svg";
import { ReactComponent as FacebookIcon } from "../../assets/icons/Facebook1.svg";
import { ReactComponent as LinkedInIcon } from "../../assets/icons/linkedIn1.svg";
import { ReactComponent as TwitterIcon } from "../../assets/icons/Twitter.svg";
import { ReactComponent as InstagramIcon } from "../../assets/icons/Instagram.svg";
import OutlinedLightButton from "../designSystem/OutlinedLightButton";

const RECAPTCHA_SCRIPT_ID = "google-recaptcha-script";

function loadRecaptcha() {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    let timeoutId;

    const waitUntilReady = () => {
      if (window.grecaptcha?.render) {
        resolve(window.grecaptcha);
        return;
      }

      attempts += 1;
      if (attempts >= 200) {
        reject(new Error("Google reCAPTCHA did not become ready"));
        return;
      }

      timeoutId = window.setTimeout(waitUntilReady, 50);
    };

    const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID);
    if (existingScript) {
      waitUntilReady();
      return;
    }

    const script = document.createElement("script");
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = waitUntilReady;
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      reject(new Error("Unable to load Google reCAPTCHA"));
    };
    document.head.appendChild(script);
  });
}

export function Recaptcha({ siteKey, onChange, resetKey }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    let widgetId;

    loadRecaptcha()
      .then((grecaptcha) => {
        if (!isMounted || !containerRef.current) return;

        widgetId = grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onChange(token),
          "expired-callback": () => onChange(""),
          "error-callback": () => onChange(""),
        });
        widgetIdRef.current = widgetId;
      })
      .catch(() => onChange(""));

    return () => {
      isMounted = false;
      if (widgetId !== undefined && window.grecaptcha) {
        window.grecaptcha.reset(widgetId);
      }
      widgetIdRef.current = null;
    };
  }, [siteKey, onChange]);

  useEffect(() => {
    if (widgetIdRef.current !== null && window.grecaptcha?.reset) {
      window.grecaptcha.reset(widgetIdRef.current);
      onChange("");
    }
  }, [resetKey, onChange]);

  return <div ref={containerRef} />;
}

function ContactUs() {
  const [form, setForm] = useState({
    name: "",
    number: "",
    email: "",
    message: "",
    consent: false,
  });

  const [errors, setErrors] = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaKey, setRecaptchaKey] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const handleRecaptchaChange = useCallback((token) => {
    setRecaptchaToken(token);
    setErrors((prev) => ({ ...prev, recaptcha: "" }));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // clear field error as user types
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  useEffect(() => {
       setForm({
         name: "",
         number: "",
         email: "",
         message: "",
         consent: false,
       });
       setRecaptchaToken("");
       setRecaptchaKey((key) => key + 1);
       setErrors({});
  }, [location.pathname]);


  const validate = () => {
    const next = {};

    if (!form.name.trim()) next.name = "Name is required";
    if (!form.number.trim()) next.number = "Number is required";
    if (!form.email.trim()) next.email = "Email is required";
    if (!form.message.trim()) next.message = "Message is required";
    if (!form.consent)
      next.consent = "Please confirm you have read the privacy policy.";
    if (!recaptchaToken) next.recaptcha = "Please confirm you are not a robot.";

    setErrors(next);
    return Object.keys(next).length === 0;
    };
    
    // console.log("API URL:", process.env.REACT_APP_SERVER_URL);

  /**
   * Runs automatically on Google Form submit (installable trigger).
   * Sends the form response to your Node endpoint.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!validate()) return;

      const to = process.env.REACT_APP_ADVISER_EMAIL;

      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        number: form.number.trim(),
        message: form.message.trim(),
        to: (to || "").trim(),
        recaptchaToken,
      };

      const url = `${process.env.REACT_APP_SERVER_URL}/send-adviser-contact-confirmation`;

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        console.log("API error:", errText);
        return;
      }

      // optional: success UI / reset
      setForm({
        name: "",
        number: "",
        email: "",
        message: "",
        consent: false,
      });
      setRecaptchaToken("");
      setRecaptchaKey((key) => key + 1);

      setErrors({});
      console.log("Sent successfully");
    } catch (err) {
      console.log("handleSubmit error:", err);
    }
  };

  return (
    <>
      {/*  CONTACT FORM */}
      <section className="contact-form-wrap" id="contact-us-section">
        <div className="contact-us-row">
          <div className="contact-us-info">
            <h2 className="burgandy">Send us a Message</h2>
            <div
              className="divider align-left burgandy"
              style={{ width: "100%" }}
            ></div>
            <div className="content grey">
              If you have a question, need advice, or would like to explore your
              mortgage or protection options, our friendly team is here to help.
              Simply fill in the form below and we’ll get back to you as soon as
              possible. We pride ourselves on offering straightforward,
              jargon-free guidance tailored to your individual circumstances, so
              you can feel confident in the decisions you make.
            </div>
            <div className="content-semibold grey">Michael Welton</div>
            <div className="content grey">Managing Director</div>
            <div className="row">
          
              <div className="footer-contact-item">
                <div className="footer-contact-icon">
                  <PhoneIcon />
                </div>
                <div className="footer-contact-details">
                  <a href="tel:07791 445522" className="content burgandy">
                    07791 445522
                  </a>
                </div>
              </div>
              <div className="footer-contact-item">
                <EmailIcon />
                <a
                  href="mailto:michael@wppfs.co.uk"
                  className="content burgandy"
                >
                  michael@wppfs.co.uk
                </a>
              </div>
            </div>
            <div className="row social-icons">
              <div
                className="footer-contact-item"
                onClick={() =>
                  window.open("https://www.facebook.com/wppfs", "_blank")
                }
                style={{ cursor: "pointer" }}
              >
                <FacebookIcon />
              </div>
              <div
                className="footer-contact-item"
                onClick={() =>
                  window.open(
                    "https://www.linkedin.com/in/michaelwelton/",
                    "_blank",
                  )
                }
                style={{ cursor: "pointer" }}
              >
                <LinkedInIcon />
              </div>
              <div
                className="footer-contact-item"
                onClick={() =>
                  window.open("https://x.com/WppServices", "_blank")
                }
                style={{ cursor: "pointer" }}
              >
                <TwitterIcon />
              </div>
              <div
                className="footer-contact-item"
                onClick={() =>
                  window.open(
                    "https://www.instagram.com/WPPFinancialServices/",
                    "_blank",
                  )
                }
                style={{ cursor: "pointer" }}
              >
                <InstagramIcon />
              </div>
            </div>
          </div>
          <>
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              {/* <h2>Send Us A Message</h2>
              <div
                className="divider align-center"
                style={{ width: "100%" }}
              ></div> */}
              <div className="contact-field">
                <label className="content-small">Name:</label>
                <input
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  className={errors.name ? "has-error" : ""}
                />
                {/* {errors.name ? (
                  <span className="error-text">{errors.name}</span>
                ) : null} */}
              </div>

              <div className="contact-field">
                <label className="content-small">Number:</label>
                <input
                  type="tel"
                  name="number"
                  pattern="[0-9]{10,15}"
                  value={form.number}
                  onChange={handleChange}
                  required
                  className={errors.number ? "has-error" : ""}
                />
                {/* {errors.number ? (
                  <span className="error-text">{errors.number}</span>
                ) : null} */}
              </div>

              <div className="contact-field">
                <label className="content-small">Email:</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className={errors.email ? "has-error" : ""}
                  required
                />
                {errors.email ? (
                  <span className="error-text">{errors.email}</span>
                ) : null}
              </div>

              <div className="contact-field">
                <label className="content-small">Message:</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={8}
                  required
                  className={errors.message ? "has-error" : ""}
                />
                {/* {errors.message ? (
                  <span className="error-text">{errors.message}</span>
                ) : null} */}
              </div>
              <div>
                <div className="contact-consent">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={form.consent}
                    onChange={handleChange}
                    id="consent"
                  />
                  <label htmlFor="consent" className="content-small-italic">
                    <span style={{ color: "#d04444" }}>*</span>I confirm I have
                    read the{" "}
                    <span
                      // href="/privacy-policy"
                      // target="_blank"
                      // rel="noopener noreferrer"
                      style={{ textDecoration: "underline", cursor: "pointer" }}
                      onClick={() => navigate("/privacy-policy")}
                    >
                      privacy policy
                    </span>
                  </label>
                </div>
                {errors.consent ? (
                  <span className="error-text">{errors.consent}</span>
                ) : null}
              </div>
              <div className="contact-recaptcha">
                {recaptchaSiteKey ? (
                  <Recaptcha
                    siteKey={recaptchaSiteKey}
                    onChange={handleRecaptchaChange}
                    resetKey={recaptchaKey}
                  />
                ) : (
                  <span className="error-text">
                    reCAPTCHA is not configured. Add REACT_APP_RECAPTCHA_SITE_KEY.
                  </span>
                )}
                {errors.recaptcha ? (
                  <span className="error-text">{errors.recaptcha}</span>
                ) : null}
              </div>
              <OutlinedLightButton
                text={"Submit"}
                // onClick={handleSubmit}
                type="submit"
              />
            </form>
          </>
        </div>
      </section>
    </>
  );
}

export default ContactUs;
